// App.tsx
import React, { useState, useRef } from 'react';
import { Container, Button, Typography, Alert, Snackbar, Grid, Box, ListItem, List } from '@mui/material';
import { questions } from './data/questions';
import Question from './components/Question';
import { calculateScore, interpretScore } from './utils/scoring';
import bannerImage from './data/channels4_banner.jpg'
import TikTokIcon from './data/TikTok_Icon_Black_Circle.png';
import InstagramIcon from './data/Instagram_Glyph_Gradient.png';
import YouTubeIcon from './data/yt_icon_rgb.png';
import TwitterIcon from './data/logo-black.png'; 

function App() {
  const [responses, setResponses] = useState<{ [key: number]: string }>({});
  const [score, setScore] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const alertRef = useRef<HTMLDivElement | null>(null);

  const handleResponseChange = (id: number, value: string) => {
    setResponses({ ...responses, [id]: value });
  };

  const handleSubmit = () => {
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    if (Object.keys(responses).length < questions.length) {
      setOpenSnackbar(true);
      
      return;
    }

    const totalScore = calculateScore(responses);
    setScore(totalScore);
    setFeedback(interpretScore(totalScore));
    
  };

  const handleReset = () => {
    setResponses({});
    setScore(null);
    setFeedback(null);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Container maxWidth="sm" style={{ padding: '20px', backgroundColor: '#E0F7FA' }}>
      <div style={{ position: 'relative', textAlign: 'center' }}>
        <img src={bannerImage}
          alt="ADHD Banner"
          style={{ width: '100%', maxHeight: '300px', objectFit: 'cover' }} />
        <Typography sx={{ typography: { sm: 'body1', xs: 'body2' } }} variant="h5"
          gutterBottom
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'white',
          }}>
          Adult ADHD Self-Report Scale (ASRS) v1.1
        </Typography>
      </div>
       <Typography variant="body1" paragraph>
       The Adult ADHD Self-Report Scale (ASRS-v1.1) is a widely recognized screening tool designed to identify symptoms that may indicate Attention Deficit Hyperactivity Disorder (ADHD) in adults. Comprising six targeted questions, the ASRS helps assess the frequency of behaviors commonly associated with ADHD, offering insights that can prompt further evaluation by healthcare professionals.
      </Typography>
      <Typography variant="h5">
      Importance of ADHD Screening in Adults
      </Typography>
      <Typography variant="body1" paragraph>
      ADHD is often perceived as a childhood condition, but it can persist into adulthood, affecting daily functioning and quality of life. Early identification through tools like the ASRS v1.1 is crucial for:
      </Typography>
      <List sx={{ listStyleType: 'disc' }}>
        <ListItem sx={{ display: 'list-item' }}>
        Improving Personal and Professional Relationships: Understanding symptoms can lead to better communication and relationships.
        </ListItem>
        <ListItem sx={{ display: 'list-item' }}>
        Enhancing Work Performance: Managing ADHD can improve focus and productivity in the workplace.
        </ListItem>
        <ListItem sx={{ display: 'list-item' }}>
        Promoting Mental Well-being: Proper diagnosis and treatment can alleviate associated stress and anxiety.
        </ListItem>
      </List>
      <Typography variant="h5">
      Additional Resources
      </Typography>
      <Typography variant="body1" paragraph>
      For more information on ADHD and support services, consider visiting the following organizations:
      </Typography>
      <List sx={{ listStyleType: 'disc' }}>
        <ListItem sx={{ display: 'list-item' }}>
			<a href="https://www.adhdadult.uk/" target="_blank" rel="noopener noreferrer" style={{ color: '#FF9800' }}>
      ADHDadultUK - A registered charity and peer support group for adults with ADHD
			</a>	  
      </ListItem>
        <ListItem sx={{ display: 'list-item' }}>
          <a href="https://www.cdc.gov/ncbddd/adhd/index.html" target="_blank" rel="noopener noreferrer" style={{ color: '#FF9800' }}>
            Centers for Disease Control and Prevention (CDC) - ADHD Information
          </a>  
          </ListItem>
        <ListItem sx={{ display: 'list-item' }}>
          <a href="https://www.psychiatry.org/patients-families/adhd/what-is-adhd" target="_blank" rel="noopener noreferrer" style={{ color: '#FF9800' }}>
            American Psychiatric Association - What is ADHD?
          </a>  
          </ListItem>
      </List>
      <Typography variant="h5">
      Disclaimer
      </Typography>
      <Typography variant="body1" paragraph>
      Please note that the ASRS v1.1 is a screening tool and not a diagnostic instrument. A high score suggests the need for a comprehensive evaluation by a qualified healthcare professional.
      </Typography>

      <Typography variant="body2" paragraph>
      Please answer the questions below, rating yourself on each of the criteria shown using the scale on the right side of the page. As you answer each question, click the answer that best describes how you have felt and conducted yourself over the past 6 months. Please give this completed checklist to your healthcare professional if the questionnaire shows you might have reason to see a healthcare professional about an (ADHD) assesment.,
      </Typography>
	  
	  <Grid container spacing={3}>
        {questions.map((q) => (
          <Grid item xs={12} key={q.id}>
            <Question
              id={q.id}
              question={q.text}
              value={responses[q.id] || ''}
              onChange={handleResponseChange}
            />
          </Grid>
        ))}

        <Grid item xs={12} sm={6} md={4}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSubmit}
            disabled={Object.keys(responses).length < questions.length}
			style={{ fontWeight: 'bold', color: '#FFFFFF' }}
		  >
            Submit
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={handleReset}
			style={{ borderColor: '#FF9800', color: '#FF9800' }}						  
          >
            Reset
          </Button>
        </Grid>
      </Grid>

      {score !== null && (
        
        <Alert severity="info" style={{ marginTop: '20px', backgroundColor: '#FF9800', color: '#FFFFFF' }}>
          Your score indicates behaviour and/or symptoms consistant with (ADHD). {feedback}
        </Alert>
        
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message="Please answer all questions before submitting."
      />

<Box mt={5} textAlign="center">
      <Typography variant="h5">
      Connect with Me
      </Typography>
      <Typography variant="body2" paragraph>
      Thank you for learning about the Adult ADHD Self-Report Scale. This resource is brought to you by "GenX ADHD Dad", who is passionate about raising awareness and providing support for adults with ADHD and adults who suspect they have undiagnosed ADHD. If you found this information helpful, consider following "GenX ADHD Dad" on social media:
      </Typography>
      <Box display="flex" justifyContent="center" gap={2}>
        <a href="https://www.tiktok.com/@genx_adhd_dad" target="_blank" rel="noopener noreferrer">
          <img src={TikTokIcon} alt="TikTok" style={{ width: 40, height: 40 }} />
        </a>
        <a href="https://www.instagram.com/genxadhddad/" target="_blank" rel="noopener noreferrer">
          <img src={InstagramIcon} alt="Instagram" style={{ width: 40, height: 40 }} />
        </a>
        <a href="https://x.com/genxadhddad" target="_blank" rel="noopener noreferrer">
          <img src={TwitterIcon} alt="X" style={{ width: 40, height: 40 }} />
        </a>
        <a href="https://www.youtube.com/@GenxADHDDad" target="_blank" rel="noopener noreferrer">
          <img src={YouTubeIcon} alt="YouTube" style={{ width: 40, height: 40 }} />
        </a>
      </Box>
      <div ref={alertRef}>
      <Typography variant="body2" paragraph>
      Your support helps keep this website running and allows us to continue providing valuable resources.
      </Typography>
      </div>
    </Box>
    </Container>
  );
}

export default App;